import { Injectable, inject, OnDestroy } from '@angular/core';
import {
  Auth,
  signInWithPhoneNumber,
  RecaptchaVerifier,
  ConfirmationResult,
  User as FirebaseUser,
  signOut as firebaseSignOut,
  getAdditionalUserInfo,
  authState, // ✅ P1 FIX: AngularFire's zone-aware observable (replaces onAuthStateChanged)
} from '@angular/fire/auth';
import { Firestore } from '@angular/fire/firestore';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { BehaviorSubject, ReplaySubject, firstValueFrom } from 'rxjs';
import { IAuthService, VerificationSession, AuthResult, User } from './auth.interface';
import { devLog, devError, AuthWithSettings, isErrorWithCode } from '@core';
import { AnalyticsService } from './analytics.service';
import { AnalyticsEvent } from '@core/types/analytics.types';
import { environment } from '../../../environments/environment';

/**
 * Development-only debug logger for AuthService
 *
 * **WI-028**: Uses unknown type for flexible debugging data
 * - Only used in development (devLog is production no-op per CLAUDE.md)
 * - Flexible data logging for debugging
 * - PII protected via sensitive flag check
 * - H-02: Uses dev-logger utility for production PII protection
 * - WI-012a: Debug logging helper - only logs in development mode
 *
 * @param message - Log message
 * @param data - Optional data to log (use { sensitive: true } to skip)
 */
const debugLog = (message: string, data?: unknown): void => {
  // Type guard for sensitive flag
  const hasSensitiveFlag = typeof data === 'object' && data !== null && 'sensitive' in data &&
    (data as { sensitive: unknown }).sensitive === true;

  if (!hasSensitiveFlag) {
    devLog(`[AuthService] ${message}`, data || '');
  }
};

/**
 * Phone Authentication Service
 *
 * **Phase 1 Limitations** (WI-012a):
 * - OTP sessions stored in-memory only (Map)
 * - Page reload during OTP flow requires new OTP request
 * - Mobile apps (Capacitor) unaffected - no traditional page reloads
 * - Web users must complete OTP flow without reload (acceptable for MVP)
 * - Multiple simultaneous OTP sessions supported (different tabs)
 *
 * **V1.5 Migration Plan** (WI-080):
 * - Move OTP verification to Cloud Functions
 * - Server-side session management with Firestore persistence
 * - Survives page reloads for better web UX
 *
 * @see specs_and_project_plan/workitems/phase-1/WI-012-phone-auth-service.md
 * @see specs_and_project_plan/workitems/v1-5/WI-080-sms-provider-abstraction.md
 * @see specs_and_project_plan/CheckerFly_SDD.md Section 3.3 (Backend State Machine)
 */
@Injectable({
  providedIn: 'root',
})
export class AuthService implements IAuthService, OnDestroy {
  private auth = inject(Auth);
  private firestore = inject(Firestore);
  private functions = inject(Functions);
  private analytics = inject(AnalyticsService);
  private authStateSubject = new BehaviorSubject<User | null>(null);
  private recaptchaVerifier: RecaptchaVerifier | null = null;

  // AUTH-001: ReplaySubject for auth initialization (replays completion to late subscribers)
  private authReadySubject = new ReplaySubject<void>(1);

  /**
   * Map of session IDs to their timeout handles
   *
   * **Type**: ReturnType<typeof setTimeout> works in both Node.js and browser:
   * - Browser (Capacitor): Returns `number` (timeout ID)
   * - Node.js (tests): Returns `NodeJS.Timeout` object
   * - TypeScript infers correct type based on environment
   *
   * WI-012b: OTP timeout tracking for automatic session cleanup
   */
  private otpTimeouts = new Map<string, ReturnType<typeof setTimeout>>();
  private readonly OTP_TIMEOUT_MINUTES = 5;

  // WI-012a: Session map for proper OTP binding
  // NOTE: In-memory only - sessions lost on page reload (acceptable for Phase 1 mobile-first)
  // Each sessionId maps to its specific ConfirmationResult + expiry metadata
  private confirmationSessions = new Map<string, {
    result: ConfirmationResult;
    expiresAt: number;
    phoneHash: string; // For debugging/rate limiting, not sensitive
  }>();

  authState$ = this.authStateSubject.asObservable();

  constructor() {
    // AUTH-001: Check for existing user synchronously (Firebase SDK caches this)
    const currentUser = this.auth.currentUser;
    if (currentUser) {
      // User session already loaded from IndexedDB cache
      this.authStateSubject.next(this.mapUser(currentUser));
      debugLog('Auth state initialized synchronously', { uid: currentUser.uid });
    }

    // ✅ P1 FIX: Use AngularFire's authState() observable (zone-aware)
    // This replaces onAuthStateChanged and eliminates "Injection context" warnings
    // Reference: CONSOLE_WARNINGS_AND_ERRORS_AUDIT.md (P1 Issue #2)
    authState(this.auth).subscribe((firebaseUser) => {
      debugLog('Auth state changed', { uid: firebaseUser?.uid }); // Only log non-sensitive UID
      this.authStateSubject.next(firebaseUser ? this.mapUser(firebaseUser) : null);

      // AUTH-001: Signal that auth is ready after first emission
      // Complete the subject so APP_INITIALIZER can proceed
      this.authReadySubject.next();
      this.authReadySubject.complete();
    });
  }

  // AUTH-001: Method for APP_INITIALIZER to wait for auth initialization
  /**
   * Waits for Firebase Auth to complete initialization.
   *
   * **Usage**: Called by APP_INITIALIZER in app.config.ts to block routing
   * until auth state is ready.
   *
   * **Returns**: Promise that resolves when:
   * - onAuthStateChanged callback fires (first time)
   * - User is logged in OR confirmed logged out
   *
   * **Timing**: ~50-200ms (Firebase loads session from IndexedDB)
   */
  waitForAuthReady(): Promise<void> {
    return firstValueFrom(this.authReadySubject);
  }

  // WI-012a: Clear reCAPTCHA to prevent memory leaks
  private clearRecaptcha(): void {
    if (this.recaptchaVerifier) {
      try {
        this.recaptchaVerifier.clear();
        this.recaptchaVerifier = null;
        debugLog('reCAPTCHA cleared');
      } catch (error) {
        debugLog('Failed to clear reCAPTCHA', { error });
      }
    }
  }

  // WI-012a: Initialize reCAPTCHA with proper error handling
  private initializeRecaptcha(): void {
    if (!this.recaptchaVerifier) {
      try {
        this.recaptchaVerifier = new RecaptchaVerifier(
          this.auth,
          'recaptcha-container',
          {
            size: 'invisible',
            callback: (): void => {
              debugLog('reCAPTCHA verified successfully');
            },
            'expired-callback': (): void => {
              debugLog('reCAPTCHA expired, reinitializing');
              this.clearRecaptcha();
              this.initializeRecaptcha();
            }
          }
        );

        this.recaptchaVerifier.render().then(() => {
          debugLog('reCAPTCHA rendered');
        }).catch((error) => {
          debugLog('Failed to render reCAPTCHA', { error });
          throw error;
        });
      } catch (error) {
        debugLog('Failed to initialize reCAPTCHA', { error });
        throw error;
      }
    }
  }

  async sendOtp(phoneNumber: string): Promise<VerificationSession> {
    try {
      // Validate E.164 format
      if (!this.isValidE164(phoneNumber)) {
        throw new Error('INVALID_PHONE_FORMAT');
      }

      debugLog('Sending OTP to phone'); // Don't log the actual phone number

      // WI-012b: Check rate limit on server (production only)
      // Skip in test environment where Functions emulator may not be configured
      // HF-001 FIX: Deployed checkRateLimit function to production (2025-11-28)
      if (!environment.useEmulators) {
        await this.checkRateLimitServer(phoneNumber);
      }

      // WI-015a: Disable app verification for integration tests
      // Firebase Auth requires user interaction for reCAPTCHA in headless Chrome
      // This setting allows signInWithPhoneNumber to complete without user interaction
      // Only enabled in emulator environment (safe for testing)
      // WI-028: Type-safe alternative using AuthWithSettings from common.types.ts
      if (environment.useEmulators) {
        (this.auth as unknown as AuthWithSettings).settings.appVerificationDisabledForTesting = true;
      }

      // WI-012a: Initialize reCAPTCHA if not already done
      this.initializeRecaptcha();

      // Send OTP
      if (!this.recaptchaVerifier) {
        throw new Error('reCAPTCHA verifier not initialized');
      }

      const confirmationResult = await signInWithPhoneNumber(
        this.auth,
        phoneNumber,
        this.recaptchaVerifier
      );

      debugLog('OTP sent successfully');

      const sessionId = this.generateSessionId();
      const expiresAt = Date.now() + 5 * 60 * 1000; // 5 minutes
      const phoneHash = await this.hashPhone(phoneNumber);

      // WI-012a: Store in session map (in-memory only, properly bound to sessionId)
      this.confirmationSessions.set(sessionId, {
        result: confirmationResult,
        expiresAt,
        phoneHash
      });

      // WI-012b: Set timeout to clear session after 5 minutes
      this.setOTPTimeout(sessionId);

      // Clean up expired sessions proactively
      this.cleanupExpiredSessions();

      // WI-039: Track OTP sent event
      this.analytics.logEvent(AnalyticsEvent.AUTH_PHONE_SIGNIN_INITIATED, {
        country_code: phoneNumber.substring(0, 3), // E.g., '+1', '+44' (not PII)
      });

      return {
        sessionId,
        expiresAt: new Date(expiresAt)
      };
    } catch (error: unknown) {
      // WI-012a: Clean up reCAPTCHA on error
      this.clearRecaptcha();
      this.handleError(error); // handleError already throws mapped error
    }
  }

  async verifyOtp(sessionId: string, code: string): Promise<AuthResult> {
    try {
      debugLog('Verifying OTP code');

      // WI-012b: Clear timeout FIRST to prevent race condition
      // If we check session existence first, timeout might fire and delete session
      // between the check and the verify call
      this.clearOTPTimeout(sessionId);

      // WI-012a: Retrieve session-specific confirmation result from Map
      const session = this.confirmationSessions.get(sessionId);

      if (!session) {
        throw new Error('SESSION_EXPIRED');
      }

      // Check expiration
      if (Date.now() > session.expiresAt) {
        this.confirmationSessions.delete(sessionId);
        throw new Error('SESSION_EXPIRED');
      }

      // Verify OTP with the CORRECT confirmation result for this session
      const credential = await session.result.confirm(code);
      const firebaseUser = credential.user;

      debugLog('OTP verified successfully', { uid: firebaseUser.uid });

      // WI-012a: Clean up session after successful verification
      this.confirmationSessions.delete(sessionId);
      this.clearRecaptcha();

      // SECURITY: Use Firebase's built-in isNewUser flag (reliable)
      const additionalUserInfo = getAdditionalUserInfo(credential);
      const isNewUser = additionalUserInfo?.isNewUser ?? false;

      // Get ID token
      const token = await firebaseUser.getIdToken();

      // Map to User interface
      const user = this.mapUser(firebaseUser);

      // WI-039: Track successful authentication
      this.analytics.setUserId(firebaseUser.uid);
      this.analytics.logEvent(AnalyticsEvent.AUTH_SIGNIN_SUCCESS);

      return { user, token, isNewUser };
    } catch (error: unknown) {
      // Extract error code with type safety using type guard
      const errorCode = isErrorWithCode(error) && error.code
        ? error.code
        : 'UNKNOWN';

      // WI-039: Track verification failure
      this.analytics.logEvent(AnalyticsEvent.ERROR_AUTH_VERIFICATION_FAILED, {
        error_code: errorCode,
      });

      this.handleError(error); // handleError already throws mapped error
    }
  }

  async signOut(): Promise<void> {
    try {
      // WI-039: Track signout event
      this.analytics.logEvent(AnalyticsEvent.AUTH_SIGNOUT);

      await firebaseSignOut(this.auth);
      // WI-012a: Clean up reCAPTCHA and sessions on sign out
      this.clearRecaptcha();
      this.confirmationSessions.clear();
      // WI-012b: Clear all OTP timeouts
      this.clearAllOTPTimeouts();
      this.authStateSubject.next(null);
      debugLog('User signed out');
    } catch (error) {
      debugLog('Failed to sign out', { error });
      throw error;
    }
  }

  /**
   * WI-012b: Lifecycle hook to clean up timeouts
   * Angular calls this when service is destroyed
   */
  ngOnDestroy(): void {
    this.clearRecaptcha();
    this.clearAllOTPTimeouts();
  }

  getCurrentUser(): User | null {
    const firebaseUser = this.auth.currentUser;
    return firebaseUser ? this.mapUser(firebaseUser) : null;
  }

  async getIdToken(): Promise<string> {
    const firebaseUser = this.auth.currentUser;
    if (!firebaseUser) {
      throw new Error('NOT_AUTHENTICATED');
    }
    return await firebaseUser.getIdToken();
  }

  // Private helpers

  private mapUser(firebaseUser: FirebaseUser): User {
    return {
      uid: firebaseUser.uid,
      phoneNumber: firebaseUser.phoneNumber || '',
      email: firebaseUser.email || undefined,
      displayName: firebaseUser.displayName || undefined,
    };
  }

  private isValidE164(phone: string): boolean {
    // E.164: + followed by 1-15 digits, starting with 1-9
    // Minimum practical length: 8 chars (e.g., +1234567) for smallest valid international numbers
    return /^\+[1-9]\d{1,14}$/.test(phone) && phone.length >= 8;
  }

  private generateSessionId(): string {
    // WI-012a: Browser-compatible UUID v4 generator (security requirement)
    // Uses crypto.getRandomValues() which is available in all modern browsers
    const uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (crypto.getRandomValues(new Uint8Array(1))[0] % 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
    return `session_${uuid}`;
  }

  /**
   * WI-012a: Clean up expired sessions
   * Prevents memory leaks from abandoned OTP sessions
   */
  private cleanupExpiredSessions(): void {
    const now = Date.now();
    const expiredSessions: string[] = [];

    this.confirmationSessions.forEach((session, sessionId) => {
      if (now > session.expiresAt) {
        expiredSessions.push(sessionId);
      }
    });

    expiredSessions.forEach(sessionId => {
      this.confirmationSessions.delete(sessionId);
    });

    if (expiredSessions.length > 0) {
      debugLog('Cleaned up expired sessions', { count: expiredSessions.length });
    }
  }

  private async hashPhone(phone: string): Promise<string> {
    // WI-012a: Hash phone number for privacy-safe logging (non-sensitive identifier)
    // This is only used for analytics/debugging, not security

    // Check if crypto.subtle is available (requires secure context)
    if (typeof crypto !== 'undefined' && crypto.subtle) {
      try {
        const encoder = new TextEncoder();
        const data = encoder.encode(phone);
        const hashBuffer = await crypto.subtle.digest('SHA-256', data);
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
      } catch (error) {
        debugLog('crypto.subtle not available, using fallback hash', { error });
      }
    }

    // Fallback: Simple non-cryptographic hash for logging purposes only
    // This is acceptable for Phase 1 since it's only used in debug logs
    let hash = 0;
    for (let i = 0; i < phone.length; i++) {
      const char = phone.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return `fallback_${Math.abs(hash).toString(16)}`;
  }

  /**
   * Handle and map Firebase Auth errors to app error codes
   *
   * **WI-028**: Type-safe error handling using unknown + type guards
   *
   * @param error - Error from Firebase Auth
   * @throws Error with mapped app error code
   */
  private handleError(error: unknown): never {
    // Extract error code safely
    const errorCode = isErrorWithCode(error) && error.code
      ? error.code
      : (error instanceof Error ? error.message : 'UNKNOWN');

    // WI-012a: Only log error code and message (no sensitive data like fullError object)
    if (isErrorWithCode(error)) {
      debugLog('Firebase Auth Error', {
        code: error.code,
        message: error.message
      });
    }

    // Define our app error codes that should pass through unchanged
    const appErrorCodes = [
      'RATE_LIMIT_EXCEEDED',
      'ACCOUNT_LOCKED', // WI-012c: progressive account lockout
      'INVALID_PHONE_FORMAT',
      'PHONE_REQUIRED',
      'INVALID_OTP',
      'OTP_EXPIRED',
      'TOO_MANY_ATTEMPTS',
      'RATE_LIMITED',
      'SESSION_EXPIRED',
      'AUTH_ERROR'
    ];

    // If the error is already one of our app error codes, pass it through unchanged
    if (appErrorCodes.includes(errorCode)) {
      debugLog('Error is already app error code, passing through', { errorCode });
      throw new Error(errorCode);
    }

    // Map Firebase error codes to our app error codes
    const errorMap: Record<string, string> = {
      'auth/invalid-phone-number': 'INVALID_PHONE_FORMAT',
      'auth/missing-phone-number': 'PHONE_REQUIRED',
      'auth/quota-exceeded': 'TOO_MANY_ATTEMPTS',
      'auth/invalid-verification-code': 'INVALID_OTP',
      'auth/code-expired': 'OTP_EXPIRED',
      'auth/too-many-requests': 'RATE_LIMITED',
    };

    const mappedError = errorMap[errorCode] || 'AUTH_ERROR';
    debugLog('Error mapped to', { mappedError });

    throw new Error(mappedError);
  }

  /**
   * WI-012b: Server-side rate limiting (Phase 2 enhancement)
   * WI-012c: Progressive account lockout awareness
   *
   * **Phase 2 Change**:
   * - Moved from client-side to Cloud Function
   * - Cannot be bypassed by client modifications
   * - Uses Firestore transactions for atomic operations
   * - Hashes phone numbers for privacy
   *
   * **Rate Limits**:
   * - 5 attempts per hour per phone number
   * - 1-hour block after exceeding limit
   *
   * **WI-012c**: When the server reports a progressive account lockout
   * (`accountLocked`), this surfaces as an `ACCOUNT_LOCKED` error distinct
   * from the hourly `RATE_LIMIT_EXCEEDED` block so the UI can message it
   * differently.
   *
   * @param phoneNumber - E.164 phone number
   * @throws Error with RATE_LIMIT_EXCEEDED or ACCOUNT_LOCKED code if blocked
   */
  private async checkRateLimitServer(phoneNumber: string): Promise<void> {
    try {
      debugLog('Checking rate limit on server');

      const checkRateLimitFn = httpsCallable<
        { phoneNumber: string },
        {
          allowed: boolean;
          remainingAttempts?: number;
          error?: string;
          blockedUntil?: string;
          accountLocked?: boolean; // WI-012c: progressive lockout indicator
          unlocksAt?: string;
        }
      >(this.functions, 'checkRateLimit');

      const result = await checkRateLimitFn({ phoneNumber });

      // WI-012c: Progressive lockout takes precedence over the hourly limit
      if (result.data.accountLocked) {
        debugLog('Account locked', { unlocksAt: result.data.unlocksAt });
        throw new Error('ACCOUNT_LOCKED');
      }

      if (!result.data.allowed) {
        debugLog('Rate limit exceeded', {
          blockedUntil: result.data.blockedUntil
        });
        throw new Error('RATE_LIMIT_EXCEEDED');
      }

      debugLog('Rate limit check passed', {
        remainingAttempts: result.data.remainingAttempts
      });
    } catch (error: unknown) {
      // Type-safe check for expected block errors (re-throw)
      if (
        error instanceof Error &&
        (error.message === 'RATE_LIMIT_EXCEEDED' ||
          error.message === 'ACCOUNT_LOCKED')
      ) {
        throw error;
      }
      // Log but don't block on rate limit check failures
      devError('Rate limit check failed', error);
    }
  }

  /**
   * WI-012b: Set timeout to clear OTP session after 5 minutes
   *
   * **Purpose**:
   * - OTP codes expire after 5 minutes (Firebase default)
   * - Clear session from memory to prevent confusion
   * - Prevent memory leaks from abandoned sessions
   *
   * @param sessionId - Session identifier
   */
  private setOTPTimeout(sessionId: string): void {
    // Clear any existing timeout for this session
    this.clearOTPTimeout(sessionId);

    const timeout = setTimeout(() => {
      const session = this.confirmationSessions.get(sessionId);
      if (session) {
        debugLog('OTP session expired', { sessionId });
        this.confirmationSessions.delete(sessionId);
        this.otpTimeouts.delete(sessionId);
      }
    }, this.OTP_TIMEOUT_MINUTES * 60 * 1000);

    this.otpTimeouts.set(sessionId, timeout);
  }

  /**
   * WI-012b: Clear timeout for specific session
   *
   * **Use Cases**:
   * - Called on successful OTP verification
   * - Called when user requests new OTP
   * - Prevents timeout from firing after session is no longer valid
   *
   * @param sessionId - Session identifier
   */
  private clearOTPTimeout(sessionId: string): void {
    const timeout = this.otpTimeouts.get(sessionId);
    if (timeout) {
      clearTimeout(timeout);
      this.otpTimeouts.delete(sessionId);
      debugLog('OTP timeout cleared', { sessionId });
    }
  }

  /**
   * WI-012b: Clear all OTP timeouts
   *
   * **Use Cases**:
   * - Called on sign out
   * - Called in ngOnDestroy (service cleanup)
   * - Prevents memory leaks
   */
  private clearAllOTPTimeouts(): void {
    this.otpTimeouts.forEach((timeout) => {
      clearTimeout(timeout);
    });
    this.otpTimeouts.clear();
    debugLog('All OTP timeouts cleared');
  }

}
